import { useState } from 'react';
import { useBulkUpdate, useBulkDelete, useProjects } from '@/hooks/useTasks.ts';
import { Button, Select, Input } from './ui.tsx';
import { STATUS_LABELS } from '@/lib/grouping.ts';
import { TASK_STATUSES, PRIORITIES, PRIORITY_LABELS } from '@shared/types';
import type { TaskStatus, Priority } from '@shared/types';

/**
 * Actions for the current selection, pinned to the bottom of the viewport so
 * it stays reachable without scrolling back up a long list.
 */
export function BulkBar({ selected, onClear }: { selected: Set<string>; onClear: () => void }) {
  const bulkUpdate = useBulkUpdate();
  const bulkDelete = useBulkDelete();
  const { data: projects } = useProjects();
  const [tagText, setTagText] = useState('');

  const ids = [...selected];
  if (ids.length === 0) return null;

  const apply = (patch: Parameters<typeof bulkUpdate.mutate>[0]['patch']) =>
    bulkUpdate.mutate({ ids, patch }, { onSuccess: onClear });

  function addTags() {
    const names = tagText
      .split(',')
      .map((t) => t.trim().replace(/^#/, ''))
      .filter(Boolean);
    if (!names.length) return;
    bulkUpdate.mutate({ ids, patch: { add_tag_names: names } }, { onSuccess: () => setTagText('') });
  }

  return (
    <div className="sticky bottom-4 z-20 mt-4 flex flex-wrap items-center gap-2 rounded-md border border-line-strong bg-overlay p-2 shadow-xl shadow-black/40">
      <span className="px-1 text-xs text-ink">
        <span data-numeric>{ids.length}</span> selected
      </span>

      <span aria-hidden="true" className="h-5 w-px bg-line" />

      <span className="w-32">
        <ActionSelect
          placeholder="Status"
          onChange={(v) => apply({ status: v as TaskStatus })}
          options={TASK_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
        />
      </span>

      <span className="w-28">
        <ActionSelect
          placeholder="Priority"
          onChange={(v) => apply({ priority: Number(v) as Priority })}
          options={PRIORITIES.map((p) => ({ value: String(p), label: PRIORITY_LABELS[p] }))}
        />
      </span>

      <span className="w-36">
        <ActionSelect
          placeholder="Project"
          onChange={(v) => apply({ project_id: v === '__none__' ? null : v })}
          options={[
            { value: '__none__', label: 'No project' },
            ...(projects ?? []).map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
      </span>

      <span className="flex items-center gap-1">
        <Input
          value={tagText}
          onChange={(e) => setTagText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTags()}
          placeholder="Add tags"
          aria-label="Add tags to selection"
          className="w-28 py-1 text-xs"
        />
        <Button onClick={addTags}>Add</Button>
      </span>

      <Button onClick={() => apply({ due_at: null })}>Clear date</Button>

      <span className="ml-auto flex items-center gap-1">
        <Button
          variant="danger"
          onClick={() => {
            if (confirm(`Delete ${ids.length} task${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) {
              bulkDelete.mutate(ids, { onSuccess: onClear });
            }
          }}
        >
          Delete
        </Button>
        <Button variant="ghost" onClick={onClear}>
          Done
        </Button>
      </span>
    </div>
  );
}

/**
 * A select used as an action menu: it never keeps a value, so the same action
 * can be applied twice in a row.
 */
function ActionSelect({
  placeholder,
  onChange,
  options,
}: {
  placeholder: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return <Select value="" onChange={onChange} options={options} placeholder={placeholder} align="left" />;
}
